
import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import "../../../../core/common/customPagination/customPagination.css";

interface ApplicationPaginationProps {
  currentPage: number;
  totalPages: number;
  total: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
}

const ApplicationPagination: React.FC<ApplicationPaginationProps> = ({
  currentPage,
  totalPages,
  total,
  limit,
  hasNextPage,
  hasPrevPage,
  onPageChange,
  onLimitChange,
}) => {
  const startRecord = (currentPage - 1) * limit + 1;
  const endRecord = Math.min(currentPage * limit, total);

  const getVisiblePages = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 3) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage === 1) {
      pages.push(1, 2, "...", totalPages);
    } else if (currentPage === totalPages) {
      pages.push(1, "...", totalPages - 1, totalPages);
    } else {
      pages.push(1, "...", currentPage, "...", totalPages);
    }
    return pages;
  };

  if (totalPages < 1) return null;

  return (
    <div className="custom-pagination">
      {onLimitChange && (
        <div className="custom-pagination__limit">
          <span>Show</span>
          <select
            value={limit}
            onChange={(e) => onLimitChange(parseInt(e.target.value))}
            className="custom-pagination__select"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <span style={{ whiteSpace: "nowrap" }}>per page</span>
        </div>
      )}

      <div className="custom-pagination__info">
        Showing {startRecord} to {endRecord} of {total} records
      </div>

      <div className="custom-pagination__controls">
        <button
          onClick={() => onPageChange(1)}
          disabled={!hasPrevPage}
          className="custom-pagination__btn"
        >
          <ChevronsLeft size={16} />
        </button>
        <button
          onClick={() => hasPrevPage && onPageChange(currentPage - 1)}
          disabled={!hasPrevPage}
          className="custom-pagination__btn"
        >
          <ChevronLeft size={16} />
        </button>

        {getVisiblePages().map((p, i) =>
          typeof p === "string" ? (
            <span key={`e-${i}`} className="custom-pagination__ellipsis">
              {p}
            </span>
          ) : (
            <button
              key={p}
              onClick={() => p !== currentPage && onPageChange(p)}
              disabled={p === currentPage}
              className={`custom-pagination__btn ${
                currentPage === p ? "custom-pagination__btn--active" : ""
              }`}
            >
              {p}
            </button>
          ),
        )}

        <button
          onClick={() => hasNextPage && onPageChange(currentPage + 1)}
          disabled={!hasNextPage}
          className="custom-pagination__btn"
        >
          <ChevronRight size={16} />
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={!hasNextPage}
          className="custom-pagination__btn"
        >
          <ChevronsRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default ApplicationPagination;
